import getDomainStatus from 'api/Sagemaker/getDomainStatus';
import useClient from 'api/client';
import { useEffect, useState } from 'react';
import deleteSmDomain from 'api/Sagemaker/deleteSmDomain';
import { useAlert } from 'components/message/AlertContext';
import createSagemakerDomain from 'api/Sagemaker/createSagemakerDomain';
import updateSagemakerDomain from 'api/Sagemaker/updateSagemakerDomain';

export type DomainState = {
    message: string;
    status: string;
};
const NO_DOMAIN_ERRORS = ['Could not retrieve domain stack!'];

export default function useDomain(environmentUri?: string) {
    const client = useClient();
    const { openAlert } = useAlert();

    const [domainStatus, setDomainStatus] = useState<DomainState>();
    const [domainExists, setDomainExists] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const updateDomain = async (
        readerEvent: ProgressEvent<FileReader>,
        onClose: VoidFunction | undefined,
    ) => {
        const response = await client!.mutate(
            updateSagemakerDomain(environmentUri, readerEvent.target!.result),
        );

        if (!response.errors) {
            if (onClose) onClose();
        } else {
            openAlert({
                message: response?.errors[0]?.message,
                severity: 'alert',
            });
        }
    };

    const createDomain = async (readerEvent: ProgressEvent<FileReader>) => {
        try {
            const response = await client!.mutate(
                createSagemakerDomain(
                    environmentUri,
                    readerEvent.target!.result,
                ),
            );

            const parsedResponse = JSON.parse(
                response?.data.createSagemakerDomain,
            );

            if (!response.errors && parsedResponse.status !== 'error') {
                setDomainExists(true); // Domain exists now
            } else {
                openAlert({
                    message: parsedResponse?.message,
                    severity: 'alert',
                });
            }
        } catch (error) {
            openAlert({
                message: 'An error occurred while creating the domain.',
                severity: 'alert',
            });
        }
    };

    const deleteDomain = async () => {
        setDeleteLoading(true);

        if (client && environmentUri) {
            const response = await client.mutate(
                deleteSmDomain(environmentUri),
            );
            const parsedResponse = JSON.parse(response?.data.deleteSmDomain);
            if (parsedResponse.status !== 'error') {
                setDomainExists(false);
            } else {
                setDeleteLoading(false);
                openAlert({
                    message: parsedResponse.message,
                    severity: 'alert',
                });
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (domainStatus === undefined) {
                    setLoading(true);
                }
                const response = await client?.query(
                    getDomainStatus(environmentUri),
                );
                const status = JSON.parse(response?.data.getDomainStatus);
                setDomainStatus(status);

                setDomainExists(
                    !NO_DOMAIN_ERRORS.includes(status?.message || ''),
                );
            } finally {
                setLoading(false);
            }
        };

        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, [client, domainStatus, environmentUri]);

    return {
        domainStatus,
        domainExists,
        loading,
        deleteDomain,
        deleteLoading,
        createDomain,
        updateDomain,
    };
}
